import { Card, CheckIcon, Text } from '@lmc/design-system'
import { cssVars } from '@lmc/design-system/tokens'
import type { IncidentCaseV2Step } from '@lmc/validations'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { INCIDENT_CASE_V2_STEPS } from './incidentCaseV2Steps'

type IncidentCaseV2StepperProps = {
  /** Steps saved; only these are ticked. */
  completedSteps: ReadonlyArray<IncidentCaseV2Step>
  currentStepIndex: number
  /** While a step saves, no step can be selected. */
  isDisabled?: boolean
  /** Steps the user has reached; only these are navigable. */
  visitedStepIndexes: Set<number>
  onStepSelect: (index: number) => void
  testId?: string
}

/** One value rather than stacked booleans: each marker has exactly one look. */
type StepState = 'complete' | 'current' | 'upcoming'

/**
 * Ticked once saved. A step reached but not saved looks upcoming; whether it
 * can be selected is separate.
 */
const getStepState = (
  index: number,
  currentStepIndex: number,
  isCompleted: boolean
): StepState => {
  if (index === currentStepIndex) {
    return 'current'
  }

  return isCompleted ? 'complete' : 'upcoming'
}

/** The right-rail stepper; only visited steps can be selected. */
const IncidentCaseV2Stepper = ({
  completedSteps,
  currentStepIndex,
  isDisabled = false,
  visitedStepIndexes,
  onStepSelect,
  testId = 'incident-case-v2-stepper'
}: IncidentCaseV2StepperProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)

  return (
    <Card padding="16" data-testid={`${testId}-card`}>
      <nav aria-label={t(incidentReportsConstants.CASE_V2_STEPPER_LABEL)}>
        <StepList data-testid={testId}>
          {INCIDENT_CASE_V2_STEPS.map((step, index) => {
            const state = getStepState(
              index,
              currentStepIndex,
              completedSteps.includes(step.id)
            )
            const title = t(step.titleKey)

            return (
              <li key={step.id}>
                <StepButton
                  type="button"
                  data-testid={`${testId}-${step.id}`}
                  data-state={state}
                  aria-current={state === 'current' ? 'step' : undefined}
                  // The tick alone doesn't tell a screen reader it is complete.
                  aria-label={
                    state === 'complete'
                      ? t(
                          incidentReportsConstants.CASE_V2_STEPPER_STEP_COMPLETED,
                          {
                            step: title
                          }
                        )
                      : undefined
                  }
                  disabled={isDisabled || !visitedStepIndexes.has(index)}
                  onClick={() => onStepSelect(index)}
                >
                  <Marker $state={state}>
                    {state === 'complete' ? (
                      <CheckIcon size={16} />
                    ) : (
                      <Text variant="body-sm" weight="medium" tone="inherit">
                        {index + 1}
                      </Text>
                    )}
                  </Marker>
                  <StepLabel
                    variant="body-md"
                    weight="medium"
                    tone="inherit"
                    $state={state}
                  >
                    {title}
                  </StepLabel>
                </StepButton>
              </li>
            )
          })}
        </StepList>
      </nav>
    </Card>
  )
}

const { borderWidth, color, radius, size, spacing } = cssVars

/** 4px here plus 4px of button padding either side is the designed 12px. */
const StepList = styled.ol`
  display: flex;
  flex-direction: column;
  gap: ${spacing[50]};
  margin: 0;
  padding: ${spacing[200]};
  list-style: none;
`

const StepButton = styled.button`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[150]};
  width: 100%;
  padding: ${spacing[50]} 0;
  background: none;
  border: none;
  border-radius: ${radius[100]};
  cursor: pointer;
  text-align: left;
  color: ${color.text.primary};
  text-decoration: none;

  &:disabled {
    cursor: default;
  }

  /* The design system's own focus ring: a white gap, then the outer colour. */
  &:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 ${borderWidth[50]} ${color.border.focus.inner},
      0 0 0 ${borderWidth[100]} ${color.border.focus.outer};
  }
`

/** One look per state; `text` colours the number and the tick alike. */
const MARKER_STYLES: Record<
  StepState,
  { background: string; border: string; text: string }
> = {
  complete: {
    background: color.bg.status.success.subtle,
    border: color.bg.status.success.bold,
    text: color.bg.status.success.bold
  },
  current: {
    background: color.bg.status.info.subtle,
    border: color.border.status.info,
    text: color.text.status.info
  },
  upcoming: {
    background: color.bg.secondary,
    border: 'transparent',
    text: color.text.secondary
  }
}

const Marker = styled.span<{ $state: StepState }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${size[300]};
  height: ${size[300]};
  flex-shrink: 0;
  border-radius: ${radius.full};
  background-color: ${({ $state }) => MARKER_STYLES[$state].background};
  border: ${borderWidth[25]} solid
    ${({ $state }) => MARKER_STYLES[$state].border};
  color: ${({ $state }) => MARKER_STYLES[$state].text};
`

/** A completed, navigable step is underlined like a link. */
const StepLabel = styled(Text)<{ $state: StepState }>`
  color: ${({ $state }) =>
    $state === 'complete'
      ? color.text.link.primary.default
      : color.text.primary};
  text-decoration: ${({ $state }) =>
    $state === 'complete' ? 'underline' : 'none'};
  text-underline-offset: 2px;

  /* The design system Link's hover and press: the colour shifts, nothing else. */
  ${StepButton}:not(:disabled):hover & {
    color: ${({ $state }) =>
      $state === 'complete' ? color.text.link.primary.hover : 'inherit'};
  }

  ${StepButton}:not(:disabled):active & {
    color: ${({ $state }) =>
      $state === 'complete' ? color.text.link.primary.active : 'inherit'};
  }
`

export default IncidentCaseV2Stepper
